import React, { useState, useEffect } from "react";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "../../components/toast";
import styles from "../../styles/__login.module.scss";
import Footer from "../../components/footer/Footer";
import { APP_PATH } from "../../api/backendPath";

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Step management: 1 = send OTP, 2 = verify OTP, 3 = reset password
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Step 1 fields
  const [selectedCountry, setSelectedCountry] = useState("+964");
  const [mobileNumber, setMobileNumber] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [generatedCaptcha, setGeneratedCaptcha] = useState(generateCaptcha());

  // Step 2 fields
  const [otp, setOtp] = useState("");

  // Step 3 fields
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({});
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);

  const countries = [
    { code: "+964", iso: "iq", name: "Iraq" },
    { code: "+91", iso: "in", name: "India" },
    { code: "+966", iso: "sa", name: "Saudi Arabia" },
    { code: "+971", iso: "ae", name: "UAE" },
    { code: "+965", iso: "kw", name: "Kuwait" },
    { code: "+974", iso: "qa", name: "Qatar" },
    { code: "+973", iso: "bh", name: "Bahrain" },
    { code: "+968", iso: "om", name: "Oman" },
    { code: "+962", iso: "jo", name: "Jordan" },
    { code: "+961", iso: "lb", name: "Lebanon" },
    { code: "+20", iso: "eg", name: "Egypt" },
    { code: "+90", iso: "tr", name: "Turkey" },
    { code: "+98", iso: "ir", name: "Iran" },
    { code: "+92", iso: "pk", name: "Pakistan" },
    { code: "+880", iso: "bd", name: "Bangladesh" },
    { code: "+44", iso: "gb", name: "United Kingdom" },
    { code: "+1", iso: "us", name: "United States" },
    { code: "+49", iso: "de", name: "Germany" },
    { code: "+33", iso: "fr", name: "France" },
    { code: "+61", iso: "au", name: "Australia" },
    { code: "+86", iso: "cn", name: "China" },
    { code: "+81", iso: "jp", name: "Japan" },
    { code: "+82", iso: "kr", name: "South Korea" },
    { code: "+60", iso: "my", name: "Malaysia" },
    { code: "+63", iso: "ph", name: "Philippines" },
    { code: "+234", iso: "ng", name: "Nigeria" },
    { code: "+27", iso: "za", name: "South Africa" },
    { code: "+254", iso: "ke", name: "Kenya" },
    { code: "+55", iso: "br", name: "Brazil" },
    { code: "+52", iso: "mx", name: "Mexico" },
  ];

  const selectedCountryData =
    countries.find((country) => country.code === selectedCountry) ||
    countries[0];

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        isCountryDropdownOpen &&
        !e.target.closest("[data-country-dropdown]")
      ) {
        setIsCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCountryDropdownOpen]);

  function generateCaptcha() {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    return Array.from(
      { length: 6 },
      () => chars[Math.floor(Math.random() * chars.length)],
    ).join("");
  }

  // Countdown for the "Resend OTP" link (2 minutes). The link is hidden while the
  // timer is running and re-appears only after it reaches zero.
  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const id = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const fullMobileNumber = `${selectedCountry}${mobileNumber}`;

  // STEP 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!mobileNumber.trim()) {
      newErrors.mobile = "Please enter your mobile number.";
    } else if (!/^\d{8,12}$/.test(mobileNumber)) {
      newErrors.mobile = "Please enter a valid mobile number (8–12 digits).";
    }

    if (!captchaInput.trim()) {
      newErrors.captcha = "Please enter captcha.";
    } else if (captchaInput !== generatedCaptcha) {
      newErrors.captcha = "Captcha does not match.";
      setGeneratedCaptcha(generateCaptcha());
      setCaptchaInput("");
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordSendOtp,
        {
          mobile_number: fullMobileNumber,
        },
      );

      if (response.data.status === true) {
        toast.success("OTP sent successfully to your mobile number");
        setStep(2);
        setResendTimer(120);
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to send OTP. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!otp.trim()) {
      newErrors.otp = "Please enter the OTP.";
    } else if (otp.length < 4) {
      newErrors.otp = "OTP must be at least 4 digits.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordVerifyOtp,
        {
          mobile_number: fullMobileNumber,
          otp: otp,
        },
      );

      if (response.data.status === true) {
        toast.success("OTP verified successfully");
        setStep(3);
      } else {
        toast.error(response.data.message || "Invalid OTP");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "OTP verification failed. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // STEP 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!newPassword.trim()) {
      newErrors.newPassword = "Please enter a new password.";
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters.";
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your password.";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordReset,
        {
          mobile_number: fullMobileNumber,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      );

      if (response.data.status === true) {
        toast.success(
          "Password reset successfully! Please login with your new password.",
        );
        setTimeout(() => navigate("/"), 2000);
      } else {
        toast.error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to reset password. Please try again.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + APP_PATH.forgotPasswordSendOtp,
        {
          mobile_number: fullMobileNumber,
        },
      );
      if (response.data.status === true) {
        toast.success("OTP resent successfully");
        setResendTimer(120);
      } else {
        toast.error(response.data.message || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles["main-container"]}>
      <main className={styles["main-content"]}>
        <div className={styles["content-wrapper"]}>
          {/* Illustration */}
          <div className={styles["illustration"]}>
            <div className={styles["doctor-illustration"]}>
              <img
                src="/ForgotPassword.png"
                alt="Forgot password illustration"
                className={styles["doctor-image"]}
              />
            </div>
          </div>

          {/* Form */}
          <div className={styles["login-form-container"]}>
            <h2>{t("forgotPassword") || "Forgot Password"}</h2>

            {/* STEP 1: Send OTP */}
            {step === 1 && (
              <form onSubmit={handleSendOtp} className={styles["login-form"]}>
                {/* Mobile Number */}
                <div className={styles["form-group"]}>
                  <label htmlFor="mobile">
                    {t("mobileNumber") || "Mobile Number"}
                  </label>
                  <div className={styles["mobile-input-container"]}>
                    <div
                      className={styles["country-selector"]}
                      onClick={() =>
                        setIsCountryDropdownOpen(!isCountryDropdownOpen)
                      }
                      style={{ cursor: "pointer", position: "relative" }}
                      data-country-dropdown
                    >
                      <span className={styles["selected-flag"]}>
                        <img
                          src={`https://flagcdn.com/24x18/${selectedCountryData?.iso}.png`}
                          alt={selectedCountryData?.name}
                          style={{
                            width: "24px",
                            height: "18px",
                            borderRadius: "2px",
                            objectFit: "cover",
                          }}
                        />
                        <span className={styles["selected-countryCode"]}>
                          {selectedCountryData.code}
                        </span>
                      </span>
                      <ChevronDown
                        size={14}
                        className={styles["dropdown-arrow"]}
                      />

                      {/* Custom Dropdown */}
                      {isCountryDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            width: "220px",
                            maxHeight: "200px",
                            overflowY: "auto",
                            backgroundColor: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            zIndex: 1000,
                            marginTop: "4px",
                          }}
                        >
                          {countries.map((country) => (
                            <div
                              key={country.code}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCountry(country.code);
                                setIsCountryDropdownOpen(false);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: "13px",
                                backgroundColor:
                                  selectedCountry === country.code
                                    ? "#e6f7f7"
                                    : "#fff",
                                borderBottom: "1px solid #f3f4f6",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  "#f3f4f6")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.backgroundColor =
                                  selectedCountry === country.code
                                    ? "#e6f7f7"
                                    : "#fff")
                              }
                            >
                              <img
                                src={`https://flagcdn.com/24x18/${country.iso}.png`}
                                alt={country.name}
                                style={{
                                  width: "24px",
                                  height: "18px",
                                  borderRadius: "2px",
                                  objectFit: "cover",
                                }}
                              />
                              <span style={{ fontWeight: "500" }}>
                                {country.code}
                              </span>
                              <span style={{ color: "#6b7280" }}>
                                {country.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      id="mobile"
                      placeholder="770 123 4567"
                      value={mobileNumber}
                      onChange={(e) =>
                        setMobileNumber(e.target.value.replace(/\D/g, ""))
                      }
                      className={`${styles["phone-input"]} ${errors.mobile ? styles["input-error"] : ""}`}
                    />
                  </div>
                  {errors.mobile && (
                    <p className={styles["rule-invalid"]}>{errors.mobile}</p>
                  )}
                </div>

                {/* Captcha */}
                <div className={styles["form-group"]}>
                  <label htmlFor="captcha">
                    {t("captchaVerification", "Captcha Verification")}
                  </label>
                  <div className={styles["capcha-box"]}>
                    <span className={styles["captcha-txt"]}>
                      {generatedCaptcha}
                    </span>
                    <button
                      onClick={() => setGeneratedCaptcha(generateCaptcha())}
                      type="button"
                      className={styles["change-capcha"]}
                    >
                      <svg
                        stroke="currentColor"
                        fill="currentColor"
                        strokeWidth="0"
                        viewBox="0 0 512 512"
                        height="20px"
                        width="20px"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fill="none"
                          strokeLinecap="round"
                          strokeMiterlimit="10"
                          strokeWidth="32"
                          d="m400 148-21.12-24.57A191.43 191.43 0 0 0 240 64C134 64 48 150 48 256s86 192 192 192a192.09 192.09 0 0 0 181.07-128"
                        ></path>
                        <path d="M464 97.42V208a16 16 0 0 1-16 16H337.42c-14.26 0-21.4-17.23-11.32-27.31L436.69 86.1C446.77 76 464 83.16 464 97.42z"></path>
                      </svg>
                    </button>
                  </div>
                  <input
                    type="text"
                    id="captcha"
                    placeholder="Enter captcha"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    className={`${styles["form-input"]} ${errors.captcha ? styles["input-error"] : ""}`}
                  />
                  {errors.captcha && (
                    <p className={styles["rule-invalid"]}>{errors.captcha}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles["login-btn"]}
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : t("submit") || "Submit"}
                </button>
              </form>
            )}

            {/* STEP 2: Verify OTP */}
            {step === 2 && (
              <form onSubmit={handleVerifyOtp} className={styles["login-form"]}>
                <p
                  style={{
                    marginBottom: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  An OTP has been sent to <strong>{fullMobileNumber}</strong>{" "}
                  via WhatsApp/SMS.
                </p>

                <div className={styles["form-group"]}>
                  <label htmlFor="otp">{t("enterOtp", "Enter OTP")}</label>
                  <input
                    type="text"
                    id="otp"
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    className={`${styles["form-input"]} ${errors.otp ? styles["input-error"] : ""}`}
                    maxLength={6}
                  />
                  {errors.otp && (
                    <p className={styles["rule-invalid"]}>{errors.otp}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles["login-btn"]}
                  disabled={isLoading}
                >
                  {isLoading ? "Verifying..." : t("verifyOtp", "Verify OTP")}
                </button>

                <p
                  style={{
                    marginTop: "12px",
                    textAlign: "center",
                    fontSize: "14px",
                  }}
                >
                  {resendTimer > 0 ? (
                    <span style={{ color: "#666" }}>
                      {t("resendCodeIn", "Resend code in")}{" "}
                      {formatTimer(resendTimer)}
                    </span>
                  ) : (
                    <>
                      {t("didntReceiveCode", "Didn't receive the code?") + " "}
                      <span
                        onClick={isLoading ? undefined : handleResendOtp}
                        style={{
                          color: "#0DD0D0",
                          cursor: isLoading ? "not-allowed" : "pointer",
                          fontWeight: "600",
                          opacity: isLoading ? 0.6 : 1,
                        }}
                      >
                        {t("resendOtp", "Resend OTP")}
                      </span>
                    </>
                  )}
                </p>

                <p
                  style={{
                    marginTop: "8px",
                    textAlign: "center",
                    fontSize: "13px",
                  }}
                >
                  <span
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                      setErrors({});
                    }}
                    style={{
                      color: "#666",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    ← Back to mobile number
                  </span>
                </p>
              </form>
            )}

            {/* STEP 3: Reset Password */}
            {step === 3 && (
              <form
                onSubmit={handleResetPassword}
                className={styles["login-form"]}
              >
                <p
                  style={{
                    marginBottom: "16px",
                    color: "#666",
                    fontSize: "14px",
                  }}
                >
                  OTP verified. Please set your new password.
                </p>

                <div className={styles["form-group"]}>
                  <label htmlFor="newPassword">
                    {t("newPassword") || "New Password"}
                  </label>
                  <div className={styles["password-wrapper"]}>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="newPassword"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={`${styles["form-input"]} ${errors.newPassword ? styles["input-error"] : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className={styles["password-toggle"]}
                    >
                      {showNewPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className={styles["rule-invalid"]}>
                      {errors.newPassword}
                    </p>
                  )}
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="confirmPassword">
                    {t("confirmPassword") || "Confirm Password"}
                  </label>
                  <div className={styles["password-wrapper"]}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${styles["form-input"]} ${errors.confirmPassword ? styles["input-error"] : ""}`}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className={styles["password-toggle"]}
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className={styles["rule-invalid"]}>
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className={styles["login-btn"]}
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Resetting..."
                    : t("resetPassword") || "Reset Password"}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPassword;
